<script setup>
import { ref } from 'vue'
import { useDocumentoEntradaAcoes } from '@/composables/useDocumentoEntradaAcoes'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { SUP_ST_DOC_ENT } from '@/constants/SUP_ST_DOC_ENT'

const props = defineProps({
  documento: {
    type: Object,
    required: true
  },
  temModuloDocumento: {
    type: Boolean,
    default: false
  },
  temCompleto: {
    type: Boolean,
    default: false
  },
  temVisualizacao: {
    type: Boolean,
    default: false
  },
  podeEditar: {
    type: Boolean,
    default: false
  },
  modoExibicao: {
    type: String,
    default: 'icone' // 'icone' | 'texto'
  }
})

const emit = defineEmits([
  'show-notification',
  'editar',
  'visualizar',
  'acao-completa',
  'aprovar'
])

// ============================================================
// COMPOSABLE DE AÇÕES
// ============================================================
const {
  confirmarAcao,
  onConfirmarAcao
} = useDocumentoEntradaAcoes({
  onSuccess: (tipoAcao, documento) => {
    emit('acao-completa', { tipoAcao, documento })
  },
  showNotification: (payload) => {
    emit('show-notification', payload)
  }
})

// ============================================================
// REF PARA O MODAL DE CONFIRMAÇÃO
// ============================================================
const confirmationModalRef = ref(null)
const showAuditLog = ref(false)

// ============================================================
// HANDLERS DE CLIQUE (com stop propagation)
// ============================================================
const handleExcluir = (e) => {
  e.stopPropagation()
  confirmarAcao('excluir', props.documento, confirmationModalRef.value)
}

const handleCancelar = (e) => {
  e.stopPropagation()
  if (props.documento.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado) {
    confirmarAcao('cancelarAprovado', props.documento, confirmationModalRef.value)
  } else {
    confirmarAcao('cancelar', props.documento, confirmationModalRef.value)
  }
}

const handleAprovar = (e) => {
  e.stopPropagation()
  emit('aprovar', props.documento)
}

const handleEditar = (e) => {
  e.stopPropagation()
  emit('editar', props.documento)
}

const handleVisualizar = (e) => {
  e.stopPropagation()
  emit('visualizar', props.documento)
}

// ============================================================
// HELPERS DE VISIBILIDADE
// ============================================================
const doc = () => props.documento

const podeAcoes = () => props.temModuloDocumento && props.temCompleto

const statusEmDigitacao = () => doc().lstStatusDocumento === SUP_ST_DOC_ENT.EmDigitao

const statusAprovado = () => doc().lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado

const podeExcluir = () => podeAcoes() && statusEmDigitacao()

const podeCancelar = () => podeAcoes() && (statusEmDigitacao() || statusAprovado())

const podeAprovar = () => podeAcoes() && statusEmDigitacao()

const podeEditarRegistro = () => podeAcoes() && statusEmDigitacao()

const podeVisualizar = () =>
  props.temModuloDocumento &&
  ((props.temCompleto && statusAprovado()) || (props.temVisualizacao && !props.temCompleto))

// ============================================================
// COMPUTED PARA MODO DE EXIBIÇÃO
// ============================================================
const isModoTexto = () => props.modoExibicao === 'texto'

// Map de botões para modo texto
const botoesTexto = {
  excluir: { classe: 'btn btn-sm btn-outline-danger', icone: 'bi-trash-fill', label: 'Excluir' },
  cancelar: { classe: 'btn btn-sm btn-outline-warning', icone: 'bi-x-circle-fill', label: 'Cancelar' },
  cancelarAprovado: { classe: 'btn btn-sm btn-outline-warning', icone: 'bi-x-circle-fill', label: 'Cancelar' },
  aprovar: { classe: 'btn btn-sm btn-outline-success', icone: 'bi-check-circle-fill', label: 'Aprovar' },
  editar: { classe: 'btn btn-sm btn-outline-primary', icone: 'bi-pencil-fill', label: 'Editar' },
  visualizar: { classe: 'btn btn-sm btn-outline-secondary', icone: 'bi-eye-fill', label: 'Visualizar' }
}
</script>

<template>
  <div class="d-flex gap-1 acoes-documentos" :class="{ 'modo-texto': isModoTexto() }">
    <!-- Auditoria -->
    <button
      v-if="props.documento?.idDocumentoEntrada"
      type="button"
      :class="isModoTexto() ? 'btn btn-sm btn-outline-secondary' : 'btn-icon'"
      :title="isModoTexto() ? '' : 'Auditoria'"
      @click="showAuditLog = true"
    >
      <i class="bi bi-clock-history"></i>
      <span v-if="isModoTexto()" class="ms-1">Auditoria</span>
    </button>

    <!-- Excluir -->
    <button
      v-if="podeExcluir()"
      type="button"
      :class="isModoTexto() ? botoesTexto.excluir.classe : 'btn-icon'"
      :title="isModoTexto() ? '' : 'Excluir Documento'"
      @click="handleExcluir"
    >
      <i :class="['bi', botoesTexto.excluir.icone]"></i>
      <span v-if="isModoTexto()" class="ms-1">{{ botoesTexto.excluir.label }}</span>
    </button>

    <!-- Cancelar -->
    <button
      v-if="podeCancelar()"
      type="button"
      :class="isModoTexto() ? (statusAprovado() ? botoesTexto.cancelarAprovado.classe : botoesTexto.cancelar.classe) : 'btn-icon'"
      :title="isModoTexto() ? '' : (statusAprovado() ? 'Funcionalidade em construção' : 'Cancelar Documento')"
      @click="handleCancelar"
    >
      <i :class="['bi', statusAprovado() ? botoesTexto.cancelarAprovado.icone : botoesTexto.cancelar.icone]"></i>
      <span v-if="isModoTexto()" class="ms-1">{{ botoesTexto.cancelar.label }}</span>
    </button>

    <!-- Aprovar -->
    <button
      v-if="podeAprovar()"
      type="button"
      :class="isModoTexto() ? botoesTexto.aprovar.classe : 'btn-icon'"
      :title="isModoTexto() ? '' : 'Aprovar Documento'"
      @click="handleAprovar"
    >
      <i :class="['bi', botoesTexto.aprovar.icone]"></i>
      <span v-if="isModoTexto()" class="ms-1">{{ botoesTexto.aprovar.label }}</span>
    </button>

    <!-- Editar -->
    <button
      v-if="podeEditarRegistro()"
      type="button"
      :class="isModoTexto() ? botoesTexto.editar.classe : 'btn-icon'"
      :title="isModoTexto() ? '' : 'Editar Documento'"
      @click="handleEditar"
    >
      <i :class="['bi', botoesTexto.editar.icone]"></i>
      <span v-if="isModoTexto()" class="ms-1">{{ botoesTexto.editar.label }}</span>
    </button>

    <!-- Visualizar -->
    <button
      v-if="podeVisualizar()"
      type="button"
      :class="isModoTexto() ? botoesTexto.visualizar.classe : 'btn-icon'"
      :title="isModoTexto() ? '' : 'Visualizar Documento'"
      @click="handleVisualizar"
    >
      <i :class="['bi', botoesTexto.visualizar.icone]"></i>
      <span v-if="isModoTexto()" class="ms-1">{{ botoesTexto.visualizar.label }}</span>
    </button>

    <ConfirmationModal
      ref="confirmationModalRef"
      @confirmed="onConfirmarAcao"
    />

    <AuditLogViewer
      v-if="showAuditLog"
      tabela="DocumentoEntrada"
      :chave-primaria="props.documento.idDocumentoEntrada"
      @fechar="showAuditLog = false"
    />
  </div>
</template>

<style scoped>
/* ============================================================ */
/* MODO ÍCONE: botões redondos minimalistas                     */
/* ============================================================ */
.acoes-documentos:not(.modo-texto) :deep(.btn-icon) {
  background: none;
  border: none;
  color: #6c7d8e;
  padding: 0.4rem;
  border-radius: 50%;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}

.acoes-documentos:not(.modo-texto) :deep(.btn-icon:hover) {
  background-color: #e2e6ea;
  color: #212934;
}

[data-bs-theme="dark"] .acoes-documentos:not(.modo-texto) :deep(.btn-icon:hover) {
  background-color: #343a40;
  color: #fff;
}

.acoes-documentos:not(.modo-texto) :deep(.btn-icon:focus-visible) {
  outline: 2px solid var(--bs-info);
  outline-offset: 2px;
}

/* ============================================================ */
/* MODO TEXTO: botões outline com rótulo                        */
/* ============================================================ */
.acoes-documentos.modo-texto {
  flex-wrap: wrap;
}

.acoes-documentos.modo-texto :deep(.btn-sm) {
  font-size: 0.75rem;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
}
</style>
